// El calendario y las franjas del informe se calculan en la zona horaria de Colombia.
export const REPORT_TIME_ZONE = "America/Bogota";
export const DISPLAY_HOURS = Array.from({ length: 20 }, (_, i) => i + 4);
export const TICKET_TYPES = [
  { value: "vip", label: "VIP" },
  { value: "mensualidad", label: "Mensualidad" },
  { value: "rotacion_carro", label: "Rotación Carro" },
  { value: "rotacion_moto", label: "Rotación Moto" },
  { value: "rotacion_bicicleta", label: "Rotación Bicicleta" },
];
export const ticketValue = (value) =>
  String(typeof value === "object" ? value?.value || "" : value || "")
    .trim()
    .toLowerCase();
export const formatReportDate = (value) =>
  String(value || "")
    .split("-")
    .reverse()
    .join("/");

export function validateFrequencyFilters({ startDate, endDate }) {
  const valid = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
    const date = new Date(`${value}T12:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  };
  if (!valid(startDate) || !valid(endDate)) return "Seleccione fechas de inicio y fin válidas.";
  if (startDate > endDate) return "La fecha inicial no puede ser posterior a la fecha final.";
  return null;
}

const dateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: REPORT_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  hourCycle: "h23",
});
export const todayInBogota = () => {
  const parts = Object.fromEntries(
    dateFormatter.formatToParts(new Date()).map((part) => [part.type, part.value])
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
};

function eventTime(value) {
  if (typeof value !== "string") return null;
  const local = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
  if (local) {
    if (
      validateFrequencyFilters({ startDate: local[1], endDate: local[1] }) ||
      Number(local[2]) > 23 ||
      Number(local[3]) > 59 ||
      Number(local[4] || 0) > 59
    )
      return null;
    return { date: local[1], hour: Number(local[2]) };
  }
  if (!/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(value)) return null;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  const parts = Object.fromEntries(
    dateFormatter.formatToParts(instant).map((part) => [part.type, part.value])
  );
  return { date: `${parts.year}-${parts.month}-${parts.day}`, hour: Number(parts.hour) };
}

export function processFrequencyData(events, filters = {}) {
  const error = validateFrequencyFilters(filters);
  if (error) throw new Error(error);
  const { startDate, endDate } = filters;
  const selected = (filters.ticketTypes || []).map(ticketValue).filter(Boolean);
  const allHours = Array.from({ length: 24 }, (_, hour) => hour);
  const groups = new Map();
  const cursor = new Date(`${startDate}T12:00:00Z`);
  while (cursor.toISOString().slice(0, 10) <= endDate) {
    const date = cursor.toISOString().slice(0, 10);
    groups.set(date, {
      Ent: {
        fecha: date,
        tipo: "Ent",
        total: 0,
        ...Object.fromEntries(allHours.map((hour) => [hour, 0])),
      },
      Sal: {
        fecha: date,
        tipo: "Sal",
        total: 0,
        ...Object.fromEntries(allHours.map((hour) => [hour, 0])),
      },
    });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  let unclassifiedEvents = 0;
  let unverifiedVipEvents = 0;
  const ids = new Set();
  for (const event of events || []) {
    const type = event.eventtype === "GPIN" ? "Ent" : event.eventtype === "GPOUT" ? "Sal" : null;
    if (!type) continue;
    const time = eventTime(event.fecha_captura);
    if (!time) throw new Error("El historial contiene una fecha de movimiento inválida.");
    if (time.date < startDate || time.date > endDate) continue;
    const ticketType = ticketValue(event.ticket_type);
    const category = ticketValue(event.ticket_category || ticketType);
    if (!ticketType) {
      if (selected.length)
        throw new Error(
          "El historial no identifica el tipo de tarjeta. Este filtro requiere el nuevo endpoint."
        );
      unclassifiedEvents += 1;
    }
    if (selected.length && !selected.includes(ticketType) && !selected.includes(category)) continue;
    if (category === "vip" || ticketType === "vip") {
      if (!event.access_scope) {
        unverifiedVipEvents += 1;
        continue;
      }
      if (String(event.access_scope).toLowerCase() !== "general") continue;
    }
    if (event.id != null) {
      if (ids.has(String(event.id))) continue;
      ids.add(String(event.id));
    }
    const row = groups.get(time.date)[type];
    row[time.hour] += 1;
    row.total += 1;
  }
  const rows = [...groups.values()].flatMap((group) => [group.Ent, group.Sal]);
  const totals = {
    entries: Object.fromEntries(allHours.map((hour) => [hour, 0])),
    exits: Object.fromEntries(allHours.map((hour) => [hour, 0])),
  };
  let totalEntries = 0,
    totalExits = 0;
  rows.forEach((row) => {
    const target = row.tipo === "Ent" ? totals.entries : totals.exits;
    allHours.forEach((hour) => {
      target[hour] += row[hour];
    });
    if (row.tipo === "Ent") totalEntries += row.total;
    else totalExits += row.total;
  });
  const warnings = [];
  if (unclassifiedEvents)
    warnings.push(
      "El historial actual no identifica todos los tipos de tarjeta. El conteo general de VIP está pendiente de validar con el nuevo endpoint."
    );
  if (unverifiedVipEvents)
    warnings.push(
      `${unverifiedVipEvents} movimientos VIP se excluyeron porque falta identificar si corresponden a un acceso general.`
    );
  return {
    rows,
    totals,
    hours: DISPLAY_HOURS,
    totalEntries,
    totalExits,
    totalEvents: totalEntries + totalExits,
    totalDays: groups.size,
    filters: { ...filters, ticketTypes: [...(filters.ticketTypes || [])] },
    warnings,
    verified: !warnings.length,
  };
}
